import { Modal, Stack, Text, Title, Button, Group, ThemeIcon, Badge } from '@mantine/core';
import {
  IconCircleCheck,
  IconConfetti,
  IconArrowRight,
  IconBooks,
  IconHourglass,
} from '@tabler/icons-react';

export interface SessionCompletionModalProps {
  opened: boolean;
  onClose: () => void;
  /** Title of the session that just finished. */
  sessionName: string;
  /** Title of the course (used in the "course complete" copy). */
  courseName: string;
  /** Title of the next session, or null when there's nothing left. */
  nextSessionName?: string | null;
  /** Optional 1-based index for the "Session N of M" caption. */
  position?: { current: number; total: number };
  /** Fires "Start next session" — only invoked when nextSessionName is set. */
  onStartNext: () => void;
  /** Fires "Browse courses" — only invoked when there is no next session. */
  onBrowseCourses: () => void;
}

/**
 * Shown after a session's media reports completion (video/audio ended,
 * "Mark as read" tapped on PDF/reading material).
 *
 *  - When another session follows, the modal celebrates the win and offers
 *    a primary "Start next session" CTA. "Stay here" closes without nav.
 *  - When this was the final session, the modal flips to a course-complete
 *    state and offers "Browse courses" + "Back to course overview".
 */
export function SessionCompletionModal({
  opened,
  onClose,
  sessionName,
  courseName,
  nextSessionName,
  position,
  onStartNext,
  onBrowseCourses,
}: SessionCompletionModalProps) {
  const hasNext = !!nextSessionName;
  const isFinal = !hasNext && position && position.current === position.total;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton
      size="md"
      radius="lg"
      padding="xl"
      title={
        <Group gap="xs">
          <ThemeIcon
            size={32}
            radius="xl"
            variant="light"
            color={isFinal ? 'grape' : 'teal'}
          >
            {isFinal ? <IconConfetti size={18} /> : <IconCircleCheck size={18} />}
          </ThemeIcon>
          <Title order={5}>
            {isFinal ? 'Course complete!' : 'Session complete'}
          </Title>
        </Group>
      }
    >
      <Stack gap="md">
        {position && (
          <Badge variant="light" color={isFinal ? 'grape' : 'teal'} radius="xl">
            Session {position.current} of {position.total}
          </Badge>
        )}

        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            You just finished
          </Text>
          <Text fw={600}>{sessionName}</Text>
        </Stack>

        {hasNext ? (
          <Stack gap={4}>
            <Text size="sm" c="dimmed">Up next</Text>
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color="brand" size="sm" radius="xl">
                <IconHourglass size={12} />
              </ThemeIcon>
              <Text fw={500} lineClamp={2}>
                {nextSessionName}
              </Text>
            </Group>
          </Stack>
        ) : (
          <Stack gap={4}>
            <Text size="sm" c="dimmed">What now?</Text>
            <Text>
              You've reached the end of <b>{courseName}</b>. Take a moment, then explore
              another course tailored to your ministry.
            </Text>
          </Stack>
        )}

        <Group justify="flex-end" mt="xs" gap="xs">
          <Button variant="subtle" onClick={onClose}>
            Stay here
          </Button>
          {hasNext ? (
            <Button
              onClick={() => {
                onClose();
                onStartNext();
              }}
              rightSection={<IconArrowRight size={16} />}
            >
              Start next session
            </Button>
          ) : (
            <Button
              onClick={() => {
                onClose();
                onBrowseCourses();
              }}
              leftSection={<IconBooks size={16} />}
            >
              Browse courses
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
